import React, { useEffect, useState } from 'react';
import { getAdminClients, getClientDetails } from '../api/api';
import styles from './AdminLists.module.css';
import MissionDetailsModal from '../components/MissionDetailsModal';
import FilterBar from '../components/FilterBar';

const ClientsPage = () => {
    const [clients, setClients] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, total_pages: 1 });
    const [loadingList, setLoadingList] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [filters, setFilters] = useState({
        hasEmail: '',
        hasSiret: '',
    });
    
    const [selectedClient, setSelectedClient] = useState(null); 
    const [clientDetails, setClientDetails] = useState(null); 
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState('infos');
    const [selectedMissionId, setSelectedMissionId] = useState(null);

    const fetchClientsData = async (targetPage, currentSearch, currentFilters) => {
        setLoadingList(true);
        try {
            // Ici on appelle ta fonction exportée avec TOUS les paramètres
            const response = await getAdminClients(targetPage, 10, currentSearch, currentFilters);
            
            setClients(response.data.data);
            setPagination(response.data.pagination);
        } catch (err) {
            console.error("Erreur API", err);
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        fetchClientsData(pagination.page, searchTerm, filters);
    }, [pagination.page]);

    // 3. Gestionnaire de filtres amélioré
    const handleFilterChange = (name, value) => {
        const updatedFilters = { ...filters, [name]: value };
        setFilters(updatedFilters);
        
        if (pagination.page !== 1) {
            setPagination(prev => ({ ...prev, page: 1 }));
        } else {
            // Si on était déjà page 1, le useEffect ne verra pas de changement,
            // donc on force l'appel ici.
            fetchClientsData(1, searchTerm, updatedFilters);
        }
    };

    const handleSearch = (e) => {
        if(e) e.preventDefault();
        if (pagination.page !== 1) {
            setPagination(prev => ({ ...prev, page: 1 }));
        } else {
            fetchClientsData(1, searchTerm, filters);
        }
    };

    const handleResetFilters = () => {
        const reset = { hasEmail: '', hasSiret: '' };
        setFilters(reset);
        setSearchTerm('');
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchClientsData(1, '', reset);
    };

    const filterConfig = [
        {
            type: 'select',
            name: 'hasEmail',
            label: 'A un Email',
            value: filters.hasEmail,
            options: [
                { value: 'yes', label: 'Oui' },
                { value: 'no', label: 'Non' },
            ]
        },
        {
            type: 'select',
            name: 'hasSiret',
            label: 'A un SIRET',
            value: filters.hasSiret,
            options: [
                { value: 'yes', label: 'Oui' },
                { value: 'no', label: 'Non' },
            ]
        },
    ];

    const handleOpenDetails = async (clientId) => {
        setSelectedClient(clientId);
        setShowModal(true);
        setLoadingDetails(true);
        setClientDetails(null);
        setActiveTab('infos');

        try {
            const response = await getClientDetails(clientId);
            setClientDetails(response.data);
        } catch (err) {
            console.error("Erreur chargement détails", err);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedClient(null);
        setClientDetails(null);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('fr-FR');
    };

    const isMissionActive = (start, end) => {
        const now = new Date();
        return now >= new Date(start) && now <= new Date(end);
    };

    const formatSiret = (siret) => {
        if (!siret) return '-';
        if (typeof siret === 'string' && siret.includes('E+')) {
            const num = parseFloat(siret.replace(',', '.'));
            return num.toLocaleString('fullwide', { useGrouping: false });
        }
        return String(siret).replace('.0', '');
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Gestion des Partenaires</h1>
                
                <div className={styles.searchForm}>
                    <input 
                        type="text" 
                        placeholder="Rechercher (Nom, SIRET)..." 
                        className={styles.searchInput}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                    />
                    <button onClick={handleSearch} className={styles.searchBtn}>🔍</button>
                </div>
            </div>

            <FilterBar 
                filters={filterConfig}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
            />

            <div className={styles.tableWrapper}>
                {loadingList ? (
                    <div className={styles.loading}>Chargement...</div>
                ) : (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Nom Partenaire</th>
                                    <th>SIRET</th>
                                    <th>Email(s) Contact</th>
                                    <th style={{textAlign: 'center'}}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clients.map((client) => (
                                    <tr key={client.client_id}>
                                        <td className={styles.fwBold}>{client.clientName}</td>
                                        <td>{formatSiret(client.siret)}</td>
                                        <td>{client.contact_email || '-'}</td>
                                        <td style={{textAlign: 'center'}}>
                                            <button 
                                                className={styles.btnDetail}
                                                onClick={() => handleOpenDetails(client.client_id)}
                                            >
                                                Voir Détails
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {clients.length === 0 && (
                                    <tr>
                                        <td colSpan="4" style={{textAlign: 'center', padding: '40px'}}>
                                            Aucun Partenaire trouvé avec ces critères
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <div className={styles.pagination}>
                            <button 
                                disabled={pagination.page === 1}
                                onClick={() => setPagination({...pagination, page: pagination.page - 1})}
                                className={styles.pageBtn}
                            >
                                Précédent
                            </button>
                            <span className={styles.pageInfo}>
                                Page {pagination.page} / {pagination.total_pages}
                            </span>
                            <button 
                                disabled={pagination.page === pagination.total_pages}
                                onClick={() => setPagination({...pagination, page: pagination.page + 1})}
                                className={styles.pageBtn}
                            >
                                Suivant
                            </button>
                        </div>
                    </>
                )}
            </div>

            {showModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <button className={styles.closeBtn} onClick={handleCloseModal}>&times;</button>
                        
                        {loadingDetails || !clientDetails ? (
                            <div className={styles.loading}>Chargement des détails...</div>
                        ) : (
                            <>
                                <h2 className={styles.modalTitle}>{clientDetails.info.clientName}</h2>
                                
                                <div className={styles.tabs}>
                                    <button 
                                        className={`${styles.tab} ${activeTab === 'infos' ? styles.activeTab : ''}`}
                                        onClick={() => setActiveTab('infos')}
                                    >
                                        Informations
                                    </button>
                                    <button 
                                        className={`${styles.tab} ${activeTab === 'missions' ? styles.activeTab : ''}`}
                                        onClick={() => setActiveTab('missions')}
                                    >
                                        Missions ({clientDetails.missions.length})
                                    </button>
                                </div>

                                <div className={styles.tabContent}>
                                    {activeTab === 'infos' && (
                                        <div className={styles.infoGrid}>
                                            <div className={styles.infoItem}>
                                                <label>SIREN</label>
                                                <span>{formatSiret(clientDetails.info.siren)}</span>
                                            </div>
                                            <div className={styles.infoItem}>
                                                <label>SIRET</label>
                                                <span>{formatSiret(clientDetails.info.siret)}</span>
                                            </div>
                                            <div className={styles.infoItem}>
                                                <label>TVA Intra.</label>
                                                <span>{clientDetails.info.tva_number || '-'}</span>
                                            </div>
                                            <div className={styles.infoItem}>
                                                <label>Contact Principal</label>
                                                <span>{clientDetails.info.contactName || '-'}</span>
                                            </div>
                                            <div className={styles.infoItem}>
                                                <label>Email(s)</label>
                                                <span className={styles.emailLink}>{clientDetails.info.contact_email || '-'}</span>
                                            </div>
                                            <div className={styles.infoItem}>
                                                <label>Téléphone(s)</label>
                                                <span>{clientDetails.info.contact_phone || '-'}</span>
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'missions' && (
                                        <table className={styles.tableSmall}>
                                            <thead>
                                                <tr>
                                                    <th>Consultant</th>
                                                    <th>Début</th>
                                                    <th>Fin</th>
                                                    <th>TJM</th>
                                                    <th>Statut</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {clientDetails.missions.map((m, idx) => {
                                                    const active = isMissionActive(m.startDate, m.endDate);
                                                    return (
                                                        <tr key={idx}>
                                                            <td>{m.lastName} {m.firstName}</td>
                                                            <td>{formatDate(m.startDate)}</td>
                                                            <td>{formatDate(m.endDate)}</td>
                                                            <td>{m.TJM}€</td>
                                                            <td>
                                                                <span className={active ? styles.badgeSuccess : styles.badgeInfo}>
                                                                    {active ? 'Active' : 'Terminée'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <button 
                                                                    className={styles.btnDetail} 
                                                                    style={{padding: '2px 8px', fontSize: '0.8rem'}}
                                                                    onClick={() => setSelectedMissionId(m.prestation_id)}
                                                                >
                                                                    Voir
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </>
                        )}

                        {selectedMissionId && (
                            <MissionDetailsModal 
                                missionId={selectedMissionId} 
                                onClose={() => setSelectedMissionId(null)} 
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientsPage;